import {
  ChangeEvent,
  FC,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { AuthContext, Form, Payment } from '@repo-level/core-front';
import { useTranslation } from 'react-i18next';
import { useCurrentRoute } from 'react-navi';
import { isNumber } from 'lodash';
import { useFieldApi, useFieldState } from 'informed';

import { Button } from 'components/buttons';
import { FormGroup } from 'components/form/groups/FormGroup';
import { Input } from 'components/form/Input';
import { CurrencyCode, fixedAmount } from 'helpers/currency';
import {
  amountLimit,
  composeValidators,
  maskAmount,
  required,
} from 'helpers/validators';
import {
  getDefaultAmounts,
  HIGHER_PRESET,
  mapPresets,
  mapPresetsNoFTD,
} from '../helpers';
import { getUpgradeVipPrice } from 'modules/vip/components/helpers';
import { OfUnion } from 'types/common';
import { PaymentMethodContext } from '../providers';
import { LocalStorageDeposit } from 'helpers/common';
import { useAnalytics } from 'helpers/analytics';
import { useMedia } from 'modules/common/hooks/useMedia';
import { AnalyticsEventName } from 'api/analytics';
import { useMaxAmountValue } from 'hooks/wallet';
import { groupMember, USER_GROUP } from 'helpers/user';

import { ScFieldAmountWrapper, ScShortAmount } from './styled';

interface ShortAmountProps {
  min: number | string;
  max: number | string;
  field: string;
}

const DEFAULT_AMOUNT_VALUE_MIN = 10;
const DEFAULT_AMOUNT_VALUE_MAX = 50;

const ShortAmount: FC<ShortAmountProps> = ({ min, field, max }) => {
  const {
    i18n: { language: locale },
  } = useTranslation();

  const fieldState = useFieldState(field);

  const { user, pendingUser, setCurrentAmount } = useContext(AuthContext);
  const { sendEventToAnalytics } = useAnalytics();

  const fieldApi = useFieldApi(field);

  const isNoDepositUser = useMemo(
    () => [USER_GROUP.SIGN_UPS].some((group) => groupMember(group)(user)),
    [user]
  );

  const onChangeValue = (value: string) => {
    try {
      fieldApi.setValue(value);
      setCurrentAmount(Number(value));

      LocalStorageDeposit.push({ amount: value });

      sendEventToAnalytics({
        name: AnalyticsEventName.MODAL__DEPOSIT__SHORT_AMOUNT__CLICK,
        attributes: { additionalData: JSON.stringify({ amount: value }) },
      });
    } catch {}
  };

  const shortAmounts = useMemo(() => {
    const defaultAmounts = getDefaultAmounts(Number(min), Number(max));

    if (!user?.currency) return defaultAmounts;

    const currencyKey = user.currency;
    const currencyPresets = mapPresetsNoFTD[currencyKey];

    const isFirstPresetLower =
      currencyPresets && currencyPresets[0] < Number(min);
    const higherPreset = isFirstPresetLower ? HIGHER_PRESET : '';

    const templateKey = isNoDepositUser
      ? `${higherPreset}${currencyKey}`
      : `${locale}_${currencyKey}`;

    const template = isNoDepositUser
      ? mapPresetsNoFTD[templateKey]
      : mapPresets[templateKey];

    return template ?? defaultAmounts;
  }, [min, user, locale, max, isNoDepositUser]);

  const fieldAmountValue = useMemo(() => {
    return shortAmounts[1].toString();
  }, [shortAmounts]);

  const [userCurrency, setUserCurrency] = useState<string | null>(null);

  useEffect(() => {
    if (pendingUser) return;

    if (
      user?.currency &&
      user.currency !== userCurrency &&
      fieldApi.getValue() != null
    ) {
      setUserCurrency(user?.currency);

      try {
        fieldApi.setValue(fieldAmountValue);
        setCurrentAmount(Number(fieldAmountValue));
      } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fieldApi.setValue,
    pendingUser,
    userCurrency,
    fieldAmountValue,
    user?.currency,
  ]);

  useEffect(() => {
    if (
      fieldApi == null ||
      fieldApi.setValue == null ||
      fieldApi.getValue() != null
    )
      return;

    try {
      fieldApi.setValue(fieldAmountValue);
      setCurrentAmount(Number(fieldAmountValue));
    } catch {}
  }, [shortAmounts, fieldApi, field, fieldAmountValue, setCurrentAmount]);

  const shortAmountsWithCurrencySymbol = useMemo(() => {
    const currency = user?.currency as CurrencyCode;
    return shortAmounts.map((amount) => ({
      label: fixedAmount(amount, currency, 0),
      value: amount,
    }));
  }, [shortAmounts, user]);

  return (
    <ScShortAmount>
      {shortAmountsWithCurrencySymbol.map(({ label, value: btnValue }, idx) => (
        <Button
          {...(Number(fieldState?.value) === Number(btnValue)
            ? { lavenderGradient: true }
            : { grayTransparent: true })}
          medium
          autoPaddingHorizontal
          onClick={() => onChangeValue(btnValue.toString())}
          key={idx}
        >
          {label}
        </Button>
      ))}
    </ScShortAmount>
  );
};

type FieldAmountProps = OfUnion<Form.MethodField>[Form.MethodFieldType.Amount] & {
  paymentAction: Payment.Action;
  minAmountValue: number;
};

export const FieldAmount: FC<FieldAmountProps> = ({
  fieldName,
  label,
  paymentAction,
  minAmountValue,
  ...props
}) => {
  const {
    url: { query },
  } = useCurrentRoute();
  const { user, setCurrentAmount } = useContext(AuthContext);
  const { isMobileS } = useMedia();
  const fieldApi = useFieldApi(fieldName);

  const { t } = useTranslation('validation');

  const {
    paymentMethod: {
      termsOfService: { restrictions },
    },
  } = useContext(PaymentMethodContext);

  const isDeposit = paymentAction === Payment.Action.Deposit;

  const maxAmountValue = useMaxAmountValue();

  const amountValidation = useMemo(() => {
    return composeValidators(required, amountLimit(restrictions));
  }, [restrictions]);

  const depositAmount = LocalStorageDeposit.get().amount;

  useEffect(() => {
    if (depositAmount) {
      fieldApi.setValue(depositAmount);
      setCurrentAmount(depositAmount);
      return;
    }

    if (query.vip && user?.currency && fieldApi.setValue) {
      const vipAmount = getUpgradeVipPrice(user.currency);

      setCurrentAmount(Number(vipAmount));
      fieldApi.setValue(String(vipAmount));
    } else if (!isDeposit && minAmountValue && fieldApi.setValue) {
      const minAmount = minAmountValue;

      setCurrentAmount(Number(minAmount));
      fieldApi.setValue(String(minAmount));
    }
  }, [
    fieldApi,
    minAmountValue,
    isDeposit,
    query.vip,
    user?.currency,
    depositAmount,
    setCurrentAmount,
  ]);

  const handleInputChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    const inputValue = Number(target.value);

    setCurrentAmount(inputValue);

    if (
      isNumber(inputValue) &&
      maxAmountValue &&
      isNumber(maxAmountValue) &&
      inputValue > maxAmountValue
    ) {
      return fieldApi.setError(t('notEnoughMoney'));
    }
    return fieldApi.setError(null);
  };

  return (
    <FormGroup marginBottom={isMobileS ? 12 : 16}>
      <ScFieldAmountWrapper>
        <Input
          field={fieldName}
          mask={maskAmount}
          animatedPlaceholder={t('glossary:amount')}
          validate={amountValidation}
          onChange={handleInputChange}
          validateOnBlur
          onBlur={() =>
            LocalStorageDeposit.push({ amount: String(fieldApi.getValue()) })
          }
        />
        <div className="user-currency">{user?.currency}</div>
        {isDeposit && (
          <ShortAmount
            min={restrictions.minAmountValue || DEFAULT_AMOUNT_VALUE_MIN}
            max={restrictions.maxAmountValue || DEFAULT_AMOUNT_VALUE_MAX}
            field={fieldName}
            {...props}
          />
        )}
      </ScFieldAmountWrapper>
    </FormGroup>
  );
};
